// components/extras/ExtraStatusCard.js
// Card per visualizzare lo stato e le azioni dell'extra

import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  Send, Upload, CheckCircle, XCircle, Check, Download, 
  FileText, FileCheck, Package 
} from 'lucide-react';
import { extraStatusLabels, extraStatusColors, getBadgeClass, formatCurrency, formatDate } from '../../lib/utils';
import { isExtraContractEffective } from '../../lib/extraEligibility';

const ExtraStatusCard = ({
  extra,
  isAdmin,
  isTechnician,
  isAdminOrAdministration,
  processingAction,
  downloading,
  formData,
  onSendToClient,
  onApprove,
  onComplete,
  onValidate,
  onDownloadPdf,
  setShowRejectDialog,
  setShowUploadSignedDialog,
  setShowUploadSupplierDialog,
  setShowModificationDialog,
  setShowDiscountDialog
}) => {
  if (!extra) return null;
  // STEP 2C — Extra gia' contrattualmente efficace: niente annullamento distruttivo dal FE
  const isEffective = isExtraContractEffective(extra);
  return (
    <Card className="card-abitalo border-l-4" style={{ borderLeftColor: extraStatusColors[extra.approval_status] || '#666' }}>
      <CardContent className="p-4">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <Badge className={getBadgeClass(extra.approval_status)}>
                {extraStatusLabels[extra.approval_status] || extra.approval_status}
              </Badge>
              <span className="text-sm text-gray-500">
                Numero: <strong>{extra.extra_number}</strong>
              </span>
            </div>
            
            {/* Totali */}
            <div className="flex flex-wrap gap-4 text-sm">
              <span>
                Imponibile: <strong>{formatCurrency(extra.total)}</strong>
              </span>
              <span>
                IVA {extra.vat_rate || 10}%: <strong>{formatCurrency((extra.total || 0) * ((extra.vat_rate || 10) / 100))}</strong>
              </span>
              <span className="text-lg font-bold text-green-700">
                Totale: {formatCurrency(extra.total_with_vat)}
              </span>
            </div>
            
            {/* Sconto applicato */}
            {extra.discount_percentage > 0 && (
              <div className="text-sm text-amber-600">
                Sconto applicato: {extra.discount_percentage}% 
                (-{formatCurrency(extra.discount_amount || 0)})
              </div>
            )}
            
            {/* Date workflow */}
            <div className="flex flex-wrap gap-3 text-xs text-gray-500">
              {extra.requested_at && (
                <span>Richiesto: {formatDate(extra.requested_at)}</span>
              )}
              {extra.prepared_at && (
                <span>Preparato: {formatDate(extra.prepared_at)}</span>
              )}
              {extra.sent_to_client_at && (
                <span>Inviato: {formatDate(extra.sent_to_client_at)}</span>
              )}
              {extra.signed_at && (
                <span>Firmato: {formatDate(extra.signed_at)}</span>
              )}
              {extra.approved_at && (
                <span>Approvato: {formatDate(extra.approved_at)}</span>
              )}
            </div>
            
            {/* Link PDF */}
            {extra.generated_pdf_url && (
              <div className="flex items-center gap-2 mt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onDownloadPdf}
                  disabled={downloading}
                  className="text-blue-600"
                >
                  <Download className="w-4 h-4 mr-1" />
                  {downloading ? 'Generazione...' : 'Scarica PDF'}
                </Button>
                {extra.signed_pdf_url && (
                  <button
                    onClick={() => window.open(extra.signed_pdf_url, '_blank')}
                    className="flex items-center gap-1 text-sm text-green-600 hover:underline"
                  >
                    <FileCheck className="w-4 h-4" />
                    Visualizza PDF Firmato
                  </button>
                )}
              </div>
            )}
          </div>
          
          {/* Azioni in base allo stato */}
          <div className="flex flex-wrap gap-2">
            
            {/* RICHIESTO / IN_PREPARAZIONE / MODIFICA_RICHIESTA -> Salva e prepara (Admin/Amministrativo) */}
            {['richiesto', 'in_preparazione', 'modifica_richiesta', 'bozza'].includes(extra.approval_status) && isAdminOrAdministration && (
              <>
                <Button 
                  onClick={onSendToClient}
                  disabled={processingAction || formData.items.length === 0}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Pronto e Invia al Tecnico
                </Button>
                {isAdmin && (
                  <Button 
                    onClick={() => setShowDiscountDialog(true)}
                    disabled={processingAction || formData.items.length === 0}
                    variant="outline"
                  >
                    Applica Sconto
                  </Button>
                )}
              </>
            )}
            
            {/* PRONTO -> Tecnico può richiedere modifica o procedere alla firma */}
            {extra.approval_status === 'pronto' && isTechnician && (
              <Button 
                onClick={() => setShowModificationDialog(true)}
                disabled={processingAction}
                variant="outline"
                className="border-amber-500 text-amber-700 hover:bg-amber-50"
              >
                Richiedi Modifica
              </Button>
            )}
            
            {/* PRONTO -> Admin può inviare per firma */}
            {extra.approval_status === 'pronto' && isAdminOrAdministration && (
              <Button 
                onClick={onSendToClient}
                disabled={processingAction}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                <Send className="w-4 h-4 mr-2" />
                Invia per Firma
              </Button>
            )}
            
            {/* IN_ATTESA_FIRMA -> Carica Firmato */}
            {extra.approval_status === 'in_attesa_firma' && (
              <Button 
                onClick={() => setShowUploadSignedDialog(true)}
                disabled={processingAction}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="upload-signed-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                Carica PDF Firmato
              </Button>
            )}
            
            {/* FIRMATO_CLIENTE / IN_VALIDAZIONE -> Valida (Admin/Amministrativo) */}
            {['firmato_cliente', 'in_validazione'].includes(extra.approval_status) && isAdminOrAdministration && (
              <>
                <Button 
                  onClick={onValidate}
                  disabled={processingAction}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Valida Extra
                </Button>
                {!isEffective && (
                <Button 
                  onClick={() => setShowRejectDialog(true)}
                  disabled={processingAction}
                  variant="destructive"
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Rifiuta
                </Button>
                )}
                {isEffective && (
                  <span className="text-xs text-[#737373] self-center">
                    Extra accettato dal cliente: per modificarne l'importo usa una variazione in diminuzione.
                  </span>
                )}
              </>
            )}
            
            {/* VALIDATO / IN_ATTESA_ORDINE (solo forniture) -> Carica Docs Fornitore */}
            {['validato', 'in_attesa_ordine'].includes(extra.approval_status) && extra.extra_type === 'fornitura' && isAdminOrAdministration && (
              <Button 
                onClick={() => setShowUploadSupplierDialog(true)}
                disabled={processingAction}
                className="bg-purple-600 hover:bg-purple-700 text-white"
                data-testid="upload-supplier-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                Carica Ordine Fornitore
              </Button>
            )}
            
            {/* IN_ATTESA_APPROVAZIONE (legacy) -> Approva/Rifiuta (solo Admin) */}
            {extra.approval_status === 'in_attesa_approvazione' && isAdmin && (
              <>
                <Button 
                  onClick={onApprove}
                  disabled={processingAction}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Approva Extra
                </Button>
                {!isEffective && (
                <Button 
                  onClick={() => setShowRejectDialog(true)}
                  disabled={processingAction}
                  variant="destructive"
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Rifiuta
                </Button>
                )}
              </>
            )}
            
            {/* ORDINATO / VALIDATO (per lavori) / APPROVATO (legacy) -> Completa */}
            {['ordinato', 'validato', 'approvato'].includes(extra.approval_status) && isAdminOrAdministration && (
              <Button 
                onClick={onComplete}
                disabled={processingAction}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                <Check className="w-4 h-4 mr-2" />
                Segna Completato
              </Button>
            )}
          </div>
        </div>
        
        {/* Info aggiuntive per stati specifici */}
        <ExtraStatusInfo 
          extra={extra} 
          isTechnician={isTechnician} 
          isAdminOrAdministration={isAdminOrAdministration}
          isAdmin={isAdmin}
        />
      </CardContent>
    </Card>
  );
};

// Sub-component for status-specific info messages
const ExtraStatusInfo = ({ extra, isTechnician, isAdminOrAdministration, isAdmin }) => {
  return (
    <>
      {extra.approval_status === 'richiesto' && (
        <p className="text-sm text-blue-700 mt-3 bg-blue-50 p-2 rounded">
          Il tecnico ha richiesto questo extra. Prepara le voci e i prezzi.
        </p>
      )}
      {extra.approval_status === 'in_preparazione' && (
        <p className="text-sm text-blue-700 mt-3 bg-blue-50 p-2 rounded">
          Extra in preparazione. Compila le voci e invia al tecnico quando pronto.
        </p>
      )}
      {extra.approval_status === 'pronto' && isTechnician && (
        <p className="text-sm text-green-700 mt-3 bg-green-50 p-2 rounded">
          L'extra è pronto! Mostralo al cliente. Puoi richiedere modifiche se necessario.
        </p>
      )}
      {extra.approval_status === 'modifica_richiesta' && (
        <div className="mt-3 bg-amber-50 p-3 rounded border border-amber-200">
          <p className="text-sm text-amber-800 font-medium">Modifica richiesta:</p>
          <p className="text-sm text-amber-700 mt-1">{extra.modification_notes}</p>
        </div>
      )}
      {extra.approval_status === 'in_attesa_firma' && (
        <p className="text-sm text-amber-700 mt-3 bg-amber-50 p-2 rounded">
          In attesa della firma del cliente. Una volta firmato, carica il PDF.
        </p>
      )}
      {['firmato_cliente', 'in_validazione'].includes(extra.approval_status) && !isAdminOrAdministration && (
        <p className="text-sm text-blue-700 mt-3 bg-blue-50 p-2 rounded">
          Documento firmato caricato. In attesa di validazione.
        </p>
      )}
      {extra.approval_status === 'in_attesa_ordine' && (
        <p className="text-sm text-purple-700 mt-3 bg-purple-50 p-2 rounded">
          Extra validato. Carica l'ordine del fornitore per completare.
        </p>
      )}
      {extra.approval_status === 'in_attesa_approvazione' && !isAdmin && (
        <p className="text-sm text-blue-700 mt-3 bg-blue-50 p-2 rounded">
          In attesa di approvazione da parte dell'amministratore.
        </p>
      )}
    </>
  );
};

export default ExtraStatusCard;
